// src/features/auth/AuthProvider.js
import React, {useState, useContext, useEffect, useRef} from "react";
import API from "../../api";
import Cookies from "js-cookie";
import {useLocation} from "react-router-dom";
import {setAnalyticsUser, clearAnalyticsUser} from "../../analytics";

const AuthUserContext = React.createContext();
const currentUserCookieName = "__mygolfdna_current_user__";

export const AuthProvider = ({ children }) => {
  const location = useLocation();
  const mostRecentScrollToHash = useRef("");
  const cookieCurrentUser = Cookies.get(currentUserCookieName) ? JSON.parse(Cookies.get(currentUserCookieName)) : null;
  const [user, setUser] = useState(cookieCurrentUser || null);
  const [authReady, setAuthReady] = useState(false);

  // Route hash scroll (independent of auth)
  useEffect(() => {
    if (location.hash) {
      let hash = location.hash.replace('#', '');
      const element = document.getElementById(hash);
      if (element && hash !== mostRecentScrollToHash.current) {
        element.scrollIntoView({ behavior: 'smooth' });
        mostRecentScrollToHash.current = hash;
      }
    }
  }, [location.pathname, location.hash]);

  // Hydrate ONCE from /accounts/me
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await API.get("/accounts/me");
        if (!cancelled && res?.data) {
          Cookies.set(currentUserCookieName, JSON.stringify(res.data));
          setUser(res.data);
        } else if (!cancelled) {
          Cookies.remove(currentUserCookieName);
          setUser(null);
        }
      } catch (e) {
        if (!cancelled) {
          Cookies.remove(currentUserCookieName);
          setUser(null);
        }
      } finally {
        if (!cancelled) setAuthReady(true);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // GA4 identity (MGDWD-226). Reporting was unreadable because paying members,
  // free leads, anonymous visitors and our own staff all landed in one pool,
  // and the same person on phone + laptop counted twice. `user` is the single
  // place where identity is known, so binding it here covers every entry point
  // at once: login, signup, boot-from-cookie, the /accounts/me hydration, and
  // the setUser that follows a successful purchase (free -> paid is picked up
  // because planType is in the dependency list).
  //
  // The very first page_view still goes out anonymous — the gtag snippet in
  // index.html runs before React mounts. GA4 stitches the session by its User-ID
  // reporting identity, so that costs us nothing beyond one event.
  const analyticsIdentified = useRef(false);
  useEffect(() => {
    if (user?.id) {
      analyticsIdentified.current = true;
      setAnalyticsUser(user.id, {
        // Plan name when subscribed ("Premier", "Pro", ...), "free" otherwise.
        // Lower-cased because GA4 groups user-property values verbatim and the
        // same tier reaching reports as both "Pro" and "pro" splits every row.
        memberType: user.planType ? String(user.planType).toLowerCase() : "free",
        internal: !!(user.admin || user.instructor),
      });
    } else if (analyticsIdentified.current) {
      analyticsIdentified.current = false;
      clearAnalyticsUser();
    }
    // Guarded by the ref so a plain anonymous visit does not fire a pointless
    // clear on mount — only an actual sign-out does.
  }, [user?.id, user?.planType, user?.admin, user?.instructor]);

  const signout = (callback) =>
    API.get("auth/logout").then(() => {
      Cookies.remove(currentUserCookieName);
      setUser(null);
      return callback?.();
    });

  return (
    <AuthUserContext.Provider value={{ user, setUser, signout, authReady }}>
      {children}
    </AuthUserContext.Provider>
  );
};

export const useAuth = () => useContext(AuthUserContext);
